import { Box, Portal } from '@chakra-ui/react';
import { useEffect } from 'react';
import type { ReactNode } from 'react';

interface ReportingFullScreenShellProps {
    isOpen: boolean;
    onClose: () => void;
    children: ReactNode;
    /** When set and `isOpen`, shown in a sticky header inside the white panel above `children`. */
    fullscreenToolbar?: ReactNode;
}

/**
 * When `isOpen`, portals children into a modal layer with viewport margins.
 * When closed, renders children inline (normal document flow).
 */
const ReportingFullScreenShell = ({ isOpen, onClose, children, fullscreenToolbar }: ReportingFullScreenShellProps) => {
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) {
        return <>{children}</>;
    }

    return (
        <Portal>
            <Box
                position="fixed"
                inset={0}
                p={3}
                zIndex={1300}
                pointerEvents="none"
            >
                <Box
                    position="absolute"
                    inset={0}
                    bg="blackAlpha.500"
                    pointerEvents="auto"
                    onClick={onClose}
                    aria-hidden
                />
                <Box
                    position="relative"
                    pointerEvents="auto"
                    display="flex"
                    flexDirection="column"
                    h="100%"
                    minH={0}
                    overflow="hidden"
                >
                    {fullscreenToolbar != null ? (
                        <Box
                            flex={1}
                            minH={0}
                            display="flex"
                            flexDirection="column"
                            overflow="hidden"
                            bg="white"
                            borderRadius="xl"
                            boxShadow="none"
                        >
                            <Box
                                flexShrink={0}
                                borderBottom="1px solid"
                                borderColor="gray.200"
                                bg="white"
                                borderTopRadius="xl"
                                px={6}
                                py={3}
                            >
                                {fullscreenToolbar}
                            </Box>
                            <Box flex={1} minH={0} overflow="hidden" display="flex" flexDirection="column">
                                {children}
                            </Box>
                        </Box>
                    ) : (
                        children
                    )}
                </Box>
            </Box>
        </Portal>
    );
};

export default ReportingFullScreenShell;
